import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Wallet, Building2, Plus, Trash2, ArrowLeftRight, ArrowDownCircle, ArrowUpCircle, TrendingUp, TrendingDown, DollarSign, Target, LineChart, CircleAlert, CheckCircle2, RefreshCw, Upload, Pencil } from "lucide-react";
import { toast } from "sonner";

const money = (n, ccy = "MXN") => `${ccy === "USD" ? "US$" : "$"}${(Number(n) || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const CATEGORY_LABELS = {
  cobranza: "Cobranza",
  pago_proveedor: "Pago proveedor",
  flete: "Flete",
  gasto: "Gasto",
  deposito: "Depósito",
  retiro: "Retiro",
  transferencia: "Transferencia",
  ingreso_otro: "Ingreso otro",
  saldo_inicial: "Saldo inicial",
  ajuste: "Ajuste",
};
const CATEGORIES = Object.keys(CATEGORY_LABELS);

export default function Tesoreria() {
  const [tab, setTab] = useState("resumen");
  const [accounts, setAccounts] = useState([]);
  const [movements, setMovements] = useState([]);
  const [summary, setSummary] = useState({});
  const [projection, setProjection] = useState(null);
  const [budgets, setBudgets] = useState([]);
  const [vsActual, setVsActual] = useState({ rows: [] });
  const [fx, setFx] = useState({ usd_mxn: 17.0 });
  const [budgetMonth, setBudgetMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [filters, setFilters] = useState({ account_id: "all", direction: "all", category: "all", reconciled: "all", date_from: "", date_to: "" });

  const loadAll = async () => {
    const [s, a, f] = await Promise.all([
      api.get("/treasury/summary"),
      api.get("/treasury/accounts"),
      api.get("/treasury/fx-rate"),
    ]);
    setSummary(s.data);
    setAccounts(a.data);
    setFx(f.data);
  };

  const loadMovements = async () => {
    const q = {};
    if (filters.account_id !== "all") q.account_id = filters.account_id;
    if (filters.direction !== "all") q.direction = filters.direction;
    if (filters.category !== "all") q.category = filters.category;
    if (filters.reconciled !== "all") q.reconciled = filters.reconciled;
    if (filters.date_from) q.date_from = filters.date_from;
    if (filters.date_to) q.date_to = filters.date_to;
    const { data } = await api.get("/treasury/movements", { params: q });
    setMovements(data);
  };

  const loadBudgets = async () => {
    const [b, v] = await Promise.all([
      api.get("/treasury/budgets", { params: { month: budgetMonth } }),
      api.get("/treasury/budgets/vs-actual", { params: { month: budgetMonth } }),
    ]);
    setBudgets(b.data);
    setVsActual(v.data);
  };

  const loadProjection = async () => {
    const { data } = await api.get("/treasury/projection", { params: { weeks: 4 } });
    setProjection(data);
  };

  useEffect(() => { loadAll(); }, []);
  useEffect(() => { if (tab === "movimientos") loadMovements(); }, [tab, filters]);
  useEffect(() => { if (tab === "proyeccion") loadProjection(); }, [tab]);
  useEffect(() => { if (tab === "presupuestos") loadBudgets(); }, [tab, budgetMonth]);

  return (
    <div className="max-w-screen-2xl mx-auto p-6 lg:p-8 space-y-6" data-testid="tesoreria-page">
      <header className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-outfit font-bold text-stone-900 dark:text-stone-100 flex items-center gap-2.5"><Wallet className="w-7 h-7 text-[#1E3A8A]" /> Tesorería</h1>
          <p className="text-sm text-stone-600 dark:text-stone-400 mt-1">Cajas, bancos, movimientos, transferencias, conciliación y presupuestos.</p>
        </div>
        <FxWidget fx={fx} onSaved={loadAll} />
      </header>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full flex-wrap h-auto justify-start gap-1 bg-stone-100 dark:bg-stone-900 p-1 rounded-lg">
          <TabsTrigger value="resumen" data-testid="tab-resumen" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">Resumen</TabsTrigger>
          <TabsTrigger value="cuentas" data-testid="tab-cuentas" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">Cuentas</TabsTrigger>
          <TabsTrigger value="movimientos" data-testid="tab-movimientos" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">Movimientos</TabsTrigger>
          <TabsTrigger value="transferencia" data-testid="tab-transferencia" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">Transferencia</TabsTrigger>
          <TabsTrigger value="proyeccion" data-testid="tab-proyeccion" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">Proyección</TabsTrigger>
          <TabsTrigger value="presupuestos" data-testid="tab-presupuestos" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">Presupuestos</TabsTrigger>
          <TabsTrigger value="conciliacion" data-testid="tab-conciliacion" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">Conciliación</TabsTrigger>
        </TabsList>

        <div className="mt-6">
          <TabsContent value="resumen"><Resumen summary={summary} /></TabsContent>
          <TabsContent value="cuentas"><CuentasTab accounts={accounts} onChange={loadAll} /></TabsContent>
          <TabsContent value="movimientos"><MovimientosTab movements={movements} accounts={accounts} filters={filters} setFilters={setFilters} onChange={() => { loadMovements(); loadAll(); }} /></TabsContent>
          <TabsContent value="transferencia"><TransferTab accounts={accounts} fx={fx.usd_mxn} onDone={() => { loadAll(); setTab("movimientos"); }} /></TabsContent>
          <TabsContent value="proyeccion"><ProjectionTab projection={projection} /></TabsContent>
          <TabsContent value="presupuestos"><BudgetsTab budgets={budgets} vsActual={vsActual} month={budgetMonth} setMonth={setBudgetMonth} onChange={loadBudgets} /></TabsContent>
          <TabsContent value="conciliacion"><ConciliacionTab movements={movements} accounts={accounts} onLoad={loadMovements} onChange={() => { loadMovements(); loadAll(); }} filters={filters} setFilters={setFilters} /></TabsContent>
        </div>
      </Tabs>
    </div>
  );
}

// ============================================================
// RESUMEN
// ============================================================
const Resumen = ({ summary }) => {
  const accs = summary.accounts || [];
  const mxnAccs = accs.filter((a) => a.currency === "MXN");
  const usdAccs = accs.filter((a) => a.currency === "USD");
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <KPI icon={DollarSign} label="Saldo MXN" value={money(summary.total_mxn, "MXN")} tone="blue" />
        <KPI icon={DollarSign} label="Saldo USD" value={money(summary.total_usd, "USD")} tone="emerald" />
        <KPI icon={Wallet} label="Consolidado (MXN eq.)" value={money(summary.consolidated_mxn, "MXN")} tone="navy" sub={`TC ${summary.fx_rate}`} />
        <KPI icon={LineChart} label="Neto mes" value={money(summary.net_month)} tone={summary.net_month >= 0 ? "emerald" : "amber"} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <KPI icon={ArrowDownCircle} label="Ingresos mes" value={money(summary.in_month)} tone="emerald" />
        <KPI icon={ArrowUpCircle} label="Egresos mes" value={money(summary.out_month)} tone="amber" />
        <KPI icon={ArrowDownCircle} label="Ingresos hoy" value={money(summary.in_today)} tone="emerald" />
        <KPI icon={ArrowUpCircle} label="Egresos hoy" value={money(summary.out_today)} tone="amber" />
      </div>

      <Card className="p-6 border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-outfit font-semibold text-sm uppercase tracking-[0.1em] text-stone-700 dark:text-stone-300">Saldos por cuenta</h3>
          {accs.length > 0 && <span className="text-xs text-stone-500">{accs.length} cuenta{accs.length !== 1 && "s"}</span>}
        </div>
        {accs.length === 0 ? (
          <div className="text-center py-10">
            <Wallet className="w-10 h-10 mx-auto text-stone-300 mb-2" strokeWidth={1.5} />
            <div className="text-sm text-stone-500">Aún no hay cuentas registradas.</div>
            <div className="text-xs text-stone-400 mt-1">Ve a la pestaña <b>Cuentas</b> para crear la primera.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[...mxnAccs, ...usdAccs].map((a) => (
              <div key={a.id} className="p-4 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 hover:shadow-sm transition-shadow">
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-7 h-7 rounded-md flex items-center justify-center ${a.type === "banco" ? "bg-[#1E3A8A]/10 text-[#1E3A8A]" : "bg-amber-100 text-amber-700"}`}>
                    {a.type === "banco" ? <Building2 className="w-3.5 h-3.5" /> : <Wallet className="w-3.5 h-3.5" />}
                  </div>
                  <span className="font-outfit font-semibold text-sm truncate flex-1">{a.name}</span>
                  <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-stone-100 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 text-stone-600 dark:text-stone-400 font-outfit font-semibold">{a.currency}</span>
                </div>
                <div className="text-2xl font-outfit font-bold tabular-nums text-stone-900 dark:text-stone-100">{money(a.balance, a.currency)}</div>
                {a.bank_name ? (
                  <div className="text-[10px] text-stone-500 dark:text-stone-500 mt-1.5 truncate">{a.bank_name}{a.account_number ? ` · ···${(a.account_number || "").slice(-4)}` : ""}</div>
                ) : (
                  <div className="text-[10px] text-stone-400 mt-1.5 uppercase tracking-wider font-outfit font-semibold">{a.type === "caja" ? "Efectivo" : "Bancaria"}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

// ============================================================
// CUENTAS
// ============================================================
const CuentasTab = ({ accounts, onChange }) => {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { name: "", type: "caja", currency: "MXN", initial_balance: 0, bank_name: "", account_number: "", notes: "", active: true };
  const [form, setForm] = useState(empty);
  const [toDelete, setToDelete] = useState(null);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ ...empty, ...a }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Nombre requerido");
    try {
      const payload = { ...form, initial_balance: parseFloat(form.initial_balance) || 0 };
      if (editing) await api.put(`/treasury/accounts/${editing.id}`, payload);
      else await api.post("/treasury/accounts", payload);
      toast.success(editing ? "Cuenta actualizada" : "Cuenta creada");
      setOpen(false); onChange();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const del = async () => {
    try {
      const { data } = await api.delete(`/treasury/accounts/${toDelete.id}`);
      toast.success(data.deactivated ? "Cuenta desactivada (tenía movimientos)" : "Cuenta eliminada");
      setToDelete(null); onChange();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-outfit font-semibold text-sm uppercase tracking-[0.1em] text-stone-700 dark:text-stone-300">Cuentas registradas</h3>
          <p className="text-xs text-stone-500 mt-0.5">{accounts.length} cuenta{accounts.length !== 1 && "s"} · {accounts.filter(a => a.type === "banco").length} banco(s) · {accounts.filter(a => a.type === "caja").length} caja(s)</p>
        </div>
        <Button onClick={openNew} data-testid="new-account-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]"><Plus className="w-4 h-4 mr-1.5" /> Nueva cuenta</Button>
      </div>
      <Card className="overflow-hidden border-stone-200 dark:border-stone-800 shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-stone-50 dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800">
              <tr>{["Nombre", "Tipo", "Moneda", "Banco / Cuenta", "Saldo inicial", "Saldo actual", "Estado", ""].map((h) => <th key={h} className="px-4 py-3 text-left text-[10px] font-outfit font-semibold uppercase tracking-[0.1em] text-stone-500 dark:text-stone-400">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-stone-200 dark:divide-stone-800">
              {accounts.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-16 text-center">
                    <Wallet className="w-12 h-12 mx-auto text-stone-300 mb-3" strokeWidth={1.5} />
                    <div className="text-sm text-stone-500">Sin cuentas registradas.</div>
                    <div className="text-xs text-stone-400 mt-1">Crea la primera para empezar a registrar movimientos.</div>
                  </td>
                </tr>
              ) : accounts.map((a) => (
                <tr key={a.id} data-testid={`account-row-${a.id}`} className="hover:bg-stone-50 dark:hover:bg-stone-900/30">
                  <td className="px-4 py-3 text-sm font-medium text-stone-900 dark:text-stone-100">{a.name}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-outfit font-semibold px-2 py-0.5 rounded ${a.type === "banco" ? "bg-[#1E3A8A]/10 text-[#1E3A8A]" : "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"}`}>
                      {a.type === "banco" ? <Building2 className="w-3 h-3" /> : <Wallet className="w-3 h-3" />}
                      {a.type === "banco" ? "Banco" : "Caja"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs font-outfit font-semibold text-stone-700 dark:text-stone-300">{a.currency}</td>
                  <td className="px-4 py-3 text-xs text-stone-600 dark:text-stone-400">{a.bank_name ? <span>{a.bank_name}<span className="text-stone-400"> · ···{(a.account_number || "").slice(-4)}</span></span> : "—"}</td>
                  <td className="px-4 py-3 text-xs tabular-nums text-stone-500">{money(a.initial_balance, a.currency)}</td>
                  <td className="px-4 py-3 text-sm tabular-nums font-semibold text-stone-900 dark:text-stone-100">{money(a.balance || 0, a.currency)}</td>
                  <td className="px-4 py-3 text-xs">{a.active !== false ? <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> Activa</span> : <span className="text-stone-400">Inactiva</span>}</td>
                  <td className="px-4 py-3 text-xs">
                    <div className="flex gap-1">
                      <button onClick={() => openEdit(a)} className="p-1.5 rounded-md text-stone-500 hover:text-[#1E3A8A] hover:bg-blue-50 dark:hover:bg-blue-950/40" data-testid={`edit-account-${a.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => setToDelete(a)} className="p-1.5 rounded-md text-stone-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40" data-testid={`delete-account-${a.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-lg p-6">
            <h3 className="font-outfit font-bold text-lg mb-4">{editing ? "Editar cuenta" : "Nueva cuenta"}</h3>
            <form onSubmit={submit} className="space-y-3">
              <F label="Nombre"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="account-name" placeholder="ej. Caja Planta / BBVA MXN" /></F>
              <div className="grid grid-cols-2 gap-3">
                <F label="Tipo">
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger data-testid="account-type"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="caja">Caja</SelectItem><SelectItem value="banco">Banco</SelectItem></SelectContent>
                  </Select>
                </F>
                <F label="Moneda">
                  <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                    <SelectTrigger data-testid="account-currency"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="MXN">MXN — Peso mexicano</SelectItem><SelectItem value="USD">USD — Dólar</SelectItem></SelectContent>
                  </Select>
                </F>
              </div>
              <F label={`Saldo inicial (${form.currency})`}><Input type="number" step="0.01" value={form.initial_balance} onChange={(e) => setForm({ ...form, initial_balance: e.target.value })} data-testid="account-initial-balance" /></F>
              {form.type === "banco" && (
                <div className="grid grid-cols-2 gap-3">
                  <F label="Banco"><Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} placeholder="BBVA, Banorte..." /></F>
                  <F label="Núm. cuenta"><Input value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} /></F>
                </div>
              )}
              <F label="Notas"><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} /></F>
              {editing && (
                <label className="flex items-center gap-2 cursor-pointer border-t border-stone-200 dark:border-stone-800 pt-3">
                  <input type="checkbox" checked={form.active !== false} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4 rounded border-stone-300" data-testid="account-active-checkbox" />
                  <span className="text-sm">Cuenta activa {form.active === false && <span className="text-amber-600">(desmarcado = inactiva, no aparece en selectores de movimientos/pagos)</span>}</span>
                </label>
              )}
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button type="submit" data-testid="save-account-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]">{editing ? "Guardar" : "Crear"}</Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar cuenta {toDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>Si la cuenta tiene movimientos solo se desactivará (soft-delete).</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ============================================================
// MOVIMIENTOS
// ============================================================
const MovimientosTab = ({ movements, accounts, filters, setFilters, onChange }) => {
  const [open, setOpen] = useState(false);
  const empty = { account_id: "", direction: "OUT", amount: "", movement_date: new Date().toISOString().slice(0, 10), category: "gasto", description: "", reference: "", notes: "" };
  const [form, setForm] = useState(empty);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.account_id) return toast.error("Elige cuenta");
    if (!form.description) return toast.error("Descripción requerida");
    try {
      await api.post("/treasury/movements", { ...form, amount: parseFloat(form.amount) });
      toast.success("Movimiento registrado");
      setForm(empty); setOpen(false); onChange();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const del = async (mid) => {
    if (!window.confirm("¿Eliminar este movimiento?")) return;
    try {
      await api.delete(`/treasury/movements/${mid}`);
      toast.success("Movimiento eliminado");
      onChange();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-5">
      <Card className="p-4 border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm">
        <div className="flex flex-wrap gap-3 items-end justify-between">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 flex-1">
            <F label="Cuenta">
              <Select value={filters.account_id} onValueChange={(v) => setFilters({ ...filters, account_id: v })}>
                <SelectTrigger data-testid="filter-account"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Dirección">
              <Select value={filters.direction} onValueChange={(v) => setFilters({ ...filters, direction: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="IN">Ingresos</SelectItem>
                  <SelectItem value="OUT">Egresos</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <F label="Categoría">
              <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{CATEGORY_LABELS[c]}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Desde"><Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} /></F>
            <F label="Hasta"><Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} /></F>
          </div>
          <Button onClick={() => setOpen(true)} data-testid="new-movement-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF] flex-shrink-0"><Plus className="w-4 h-4 mr-1.5" /> Nuevo movimiento</Button>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="max-h-[600px] overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-100 dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800 sticky top-0">
              <tr>{["Folio", "Fecha", "Cuenta", "Descripción", "Cat.", "Referencia", "Monto", "Origen", "Conc.", "×"].map((h) => <th key={h} className="px-2 py-2 text-left text-[10px] font-outfit font-semibold uppercase tracking-wider text-stone-600 dark:text-stone-400">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-stone-200 dark:divide-stone-800">
              {movements.length === 0 ? <tr><td colSpan={10} className="px-4 py-10 text-center text-sm text-stone-500">Sin movimientos.</td></tr> : movements.map((m) => (
                <tr key={m.id} data-testid={`mov-row-${m.id}`} className="hover:bg-stone-50 dark:hover:bg-stone-900/30">
                  <td className="px-2 py-2 text-xs font-mono text-[#1E3A8A]">{m.folio}</td>
                  <td className="px-2 py-2 text-xs tabular-nums">{m.movement_date}</td>
                  <td className="px-2 py-2 text-xs">{m.account_name}</td>
                  <td className="px-2 py-2 text-xs">{m.description}</td>
                  <td className="px-2 py-2 text-[10px] uppercase tracking-wider text-stone-500">{CATEGORY_LABELS[m.category] || m.category}</td>
                  <td className="px-2 py-2 text-xs text-stone-500">{m.reference || "—"}</td>
                  <td className={`px-2 py-2 text-xs tabular-nums font-semibold ${m.direction === "IN" ? "text-emerald-700" : "text-amber-700"}`}>{m.direction === "IN" ? "+" : "−"} {money(m.amount, m.currency)}</td>
                  <td className="px-2 py-2 text-[10px] uppercase tracking-wider text-stone-500">{m.source_type === "manual" ? "manual" : m.source_type === "payment_customer" ? "cobranza" : m.source_type === "supplier_payment" ? "pago prov." : m.source_type === "transfer" ? "transf." : m.source_type === "bank_import" ? "banco" : m.source_type}</td>
                  <td className="px-2 py-2">{m.reconciled ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <CircleAlert className="w-4 h-4 text-stone-300" />}</td>
                  <td className="px-2 py-2">{m.source_type === "manual" && <button onClick={() => del(m.id)} className="p-1 rounded hover:bg-red-50 text-red-600" data-testid={`del-mov-${m.id}`}><Trash2 className="w-3.5 h-3.5" /></button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-lg p-6">
            <h3 className="font-outfit font-bold text-lg mb-4">Nuevo movimiento manual</h3>
            <form onSubmit={submit} className="space-y-3">
              <F label="Cuenta">
                <Select value={form.account_id} onValueChange={(v) => setForm({ ...form, account_id: v })}>
                  <SelectTrigger data-testid="mov-account"><SelectValue placeholder="Elegir…" /></SelectTrigger>
                  <SelectContent>{accounts.filter((a) => a.active !== false).map((a) => <SelectItem key={a.id} value={a.id}>{a.name} — {a.currency}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <div className="grid grid-cols-2 gap-3">
                <F label="Dirección">
                  <Select value={form.direction} onValueChange={(v) => setForm({ ...form, direction: v })}>
                    <SelectTrigger data-testid="mov-direction"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="IN">Ingreso</SelectItem><SelectItem value="OUT">Egreso</SelectItem></SelectContent>
                  </Select>
                </F>
                <F label="Monto"><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="mov-amount" /></F>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <F label="Fecha"><Input type="date" value={form.movement_date} onChange={(e) => setForm({ ...form, movement_date: e.target.value })} /></F>
                <F label="Categoría">
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="mov-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.filter((c) => c !== "transferencia").map((c) => <SelectItem key={c} value={c}>{CATEGORY_LABELS[c]}</SelectItem>)}</SelectContent>
                  </Select>
                </F>
              </div>
              <F label="Descripción"><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="mov-description" placeholder="ej. Compra papelería" /></F>
              <F label="Referencia"><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="Cheque, folio, etc" /></F>
              <F label="Notas"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></F>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button type="submit" data-testid="save-mov-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]">Registrar</Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
};

// ============================================================
// TRANSFERENCIA
// ============================================================
const TransferTab = ({ accounts, fx, onDone }) => {
  const [form, setForm] = useState({ from_account_id: "", to_account_id: "", amount_out: "", amount_in: "", description: "Transferencia entre cuentas", reference: "", movement_date: new Date().toISOString().slice(0, 10) });
  const src = accounts.find((a) => a.id === form.from_account_id);
  const dst = accounts.find((a) => a.id === form.to_account_id);
  const isCross = src && dst && src.currency !== dst.currency;
  const suggestedIn = useMemo(() => {
    if (!isCross || !form.amount_out) return null;
    const amt = parseFloat(form.amount_out) || 0;
    return src.currency === "USD" ? +(amt * fx).toFixed(2) : +(amt / fx).toFixed(2);
  }, [form.amount_out, isCross, fx, src]);

  const submit = async (e) => {
    e.preventDefault();
    if (!src || !dst) return toast.error("Elige cuentas");
    if (src.id === dst.id) return toast.error("Cuenta origen y destino distintas");
    try {
      const payload = {
        from_account_id: src.id, to_account_id: dst.id,
        amount_out: parseFloat(form.amount_out),
        amount_in: isCross && form.amount_in ? parseFloat(form.amount_in) : null,
        movement_date: form.movement_date, description: form.description, reference: form.reference,
      };
      await api.post("/treasury/transfer", payload);
      toast.success("Transferencia registrada");
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  return (
    <Card className="p-6 border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm max-w-4xl">
      <div className="flex items-center gap-3 mb-5 pb-4 border-b border-stone-200 dark:border-stone-800">
        <div className="w-9 h-9 rounded-lg bg-[#1E3A8A]/10 flex items-center justify-center">
          <ArrowLeftRight className="w-5 h-5 text-[#1E3A8A]" />
        </div>
        <div>
          <h3 className="font-outfit font-bold text-lg text-stone-900 dark:text-stone-100">Transferir entre cuentas</h3>
          <p className="text-xs text-stone-500 mt-0.5">Mueve fondos entre cajas y bancos. Soporta cambio de moneda con tasa configurable.</p>
        </div>
      </div>
      <form onSubmit={submit} className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <F label="Cuenta origen">
            <Select value={form.from_account_id} onValueChange={(v) => setForm({ ...form, from_account_id: v })}>
              <SelectTrigger data-testid="transfer-from"><SelectValue placeholder="Elegir cuenta origen…" /></SelectTrigger>
              <SelectContent>{accounts.filter((a) => a.active !== false).map((a) => <SelectItem key={a.id} value={a.id}>{a.name} — {money(a.balance || 0, a.currency)}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Cuenta destino">
            <Select value={form.to_account_id} onValueChange={(v) => setForm({ ...form, to_account_id: v })}>
              <SelectTrigger data-testid="transfer-to"><SelectValue placeholder="Elegir cuenta destino…" /></SelectTrigger>
              <SelectContent>{accounts.filter((a) => a.active !== false && a.id !== form.from_account_id).map((a) => <SelectItem key={a.id} value={a.id}>{a.name} — {a.currency}</SelectItem>)}</SelectContent>
            </Select>
          </F>
        </div>

        {isCross && (
          <div className="p-3.5 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/40 text-xs text-amber-900 dark:text-amber-200 flex items-start gap-2.5">
            <RefreshCw className="w-4 h-4 text-amber-700 dark:text-amber-400 mt-0.5 flex-shrink-0" />
            <div>
              Transferencia cruzada <b>{src.currency} → {dst.currency}</b>. TC configurado: <b>{fx}</b>. Sugerido recibir: <b>{money(suggestedIn || 0, dst.currency)}</b>. Puedes sobrescribir el monto de entrada si tu banco cobró diferente.
            </div>
          </div>
        )}

        <div className={`grid grid-cols-1 gap-4 ${isCross ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
          <F label={`Sale${src ? " (" + src.currency + ")" : ""}`}><Input type="number" step="0.01" value={form.amount_out} onChange={(e) => setForm({ ...form, amount_out: e.target.value })} data-testid="transfer-amount-out" placeholder="0.00" /></F>
          {isCross && <F label={`Entra${dst ? " (" + dst.currency + ")" : ""}`}><Input type="number" step="0.01" value={form.amount_in} onChange={(e) => setForm({ ...form, amount_in: e.target.value })} placeholder={suggestedIn?.toString() || "auto"} data-testid="transfer-amount-in" /></F>}
          <F label="Fecha"><Input type="date" value={form.movement_date} onChange={(e) => setForm({ ...form, movement_date: e.target.value })} /></F>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <F label="Descripción"><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="ej. Fondear caja chica" /></F>
          <F label="Referencia"><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="No. ficha, cheque, etc." /></F>
        </div>

        <div className="flex justify-end pt-4 border-t border-stone-200 dark:border-stone-800">
          <Button type="submit" data-testid="transfer-submit-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]"><ArrowLeftRight className="w-4 h-4 mr-1.5" /> Transferir</Button>
        </div>
      </form>
    </Card>
  );
};

// ============================================================
// PROYECCION
// ============================================================
const ProjectionTab = ({ projection }) => {
  if (!projection) return <div className="text-sm text-stone-500 p-6">Cargando proyección…</div>;
  const maxAbs = Math.max(1, ...projection.weekly.map((w) => Math.max(w.expected_in, w.expected_out)));
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <KPI icon={Wallet} label="Saldo inicial (MXN eq.)" value={money(projection.starting_balance_mxn)} tone="blue" />
        <KPI icon={TrendingUp} label="Ingresos esperados 4 sem" value={money(projection.weekly.reduce((a, w) => a + w.expected_in, 0))} tone="emerald" />
        <KPI icon={TrendingDown} label="Egresos esperados 4 sem" value={money(projection.weekly.reduce((a, w) => a + w.expected_out, 0))} tone="amber" />
      </div>
      <Card className="p-6 border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-outfit font-semibold text-sm uppercase tracking-[0.1em] text-stone-700 dark:text-stone-300">Flujo semanal proyectado (MXN)</h3>
          <span className="text-xs text-stone-500">Basado en due_dates de Ventas y CxP</span>
        </div>
        <div className="space-y-3">
          {projection.weekly.map((w) => (
            <div key={w.week_index} className={`p-4 rounded-lg border ${w.warning ? "bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-900/40" : "bg-stone-50/50 dark:bg-stone-900/30 border-stone-200 dark:border-stone-800"}`} data-testid={`projection-week-${w.week_index}`}>
              <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-md bg-white dark:bg-stone-950 border border-stone-200 dark:border-stone-800 flex items-center justify-center text-[#1E3A8A] font-outfit font-bold text-sm">{w.week_index}</div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.1em] font-outfit font-semibold text-stone-500">Semana {w.week_index}</div>
                    <div className="text-sm font-outfit font-semibold text-stone-900 dark:text-stone-100">{w.week_start} → {w.week_end}</div>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-xs flex-wrap">
                  <div className="flex items-center gap-1"><ArrowDownCircle className="w-3.5 h-3.5 text-emerald-600" /><span className="tabular-nums font-semibold text-emerald-700">{money(w.expected_in)}</span></div>
                  <div className="flex items-center gap-1"><ArrowUpCircle className="w-3.5 h-3.5 text-amber-600" /><span className="tabular-nums font-semibold text-amber-700">{money(w.expected_out)}</span></div>
                  <div className="px-2 py-0.5 rounded bg-stone-100 dark:bg-stone-800">Neto <span className={`tabular-nums font-semibold ${w.net >= 0 ? "text-emerald-700" : "text-red-700"}`}>{money(w.net)}</span></div>
                  <div>Saldo: <span className={`tabular-nums font-bold ${w.warning ? "text-red-700" : "text-stone-900 dark:text-stone-100"}`}>{money(w.projected_balance_mxn)}</span></div>
                </div>
              </div>
              <div className="flex gap-0.5 h-2 rounded-full overflow-hidden bg-stone-200 dark:bg-stone-800">
                <div className="bg-emerald-500 transition-all" style={{ width: `${(w.expected_in / maxAbs) * 50}%` }}></div>
                <div className="bg-amber-500 transition-all" style={{ width: `${(w.expected_out / maxAbs) * 50}%` }}></div>
              </div>
              {w.warning && <div className="mt-3 text-xs text-red-700 dark:text-red-400 flex items-center gap-1.5 font-medium"><CircleAlert className="w-3.5 h-3.5" /> Saldo proyectado negativo · planifica cobranza o pospón egresos</div>}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

// ============================================================
// PRESUPUESTOS
// ============================================================
const BudgetsTab = ({ budgets, vsActual, month, setMonth, onChange }) => {
  const [form, setForm] = useState({ category: "flete", currency: "MXN", budgeted_amount: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.budgeted_amount) return toast.error("Monto requerido");
    try {
      await api.post("/treasury/budgets", { month, category: form.category, currency: form.currency, budgeted_amount: parseFloat(form.budgeted_amount) });
      toast.success("Presupuesto guardado");
      setForm({ category: "flete", currency: "MXN", budgeted_amount: "" });
      onChange();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const del = async (bid) => {
    if (!window.confirm("¿Eliminar presupuesto?")) return;
    await api.delete(`/treasury/budgets/${bid}`);
    onChange();
  };

  return (
    <div className="space-y-5">
      <Card className="p-5 border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-md bg-[#1E3A8A]/10 flex items-center justify-center"><Target className="w-4 h-4 text-[#1E3A8A]" /></div>
          <h3 className="font-outfit font-semibold text-sm uppercase tracking-[0.1em] text-stone-700 dark:text-stone-300">Definir presupuesto del mes</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <F label="Mes"><Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="budget-month" /></F>
          <F label="Categoría">
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="budget-category"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.filter((c) => c !== "transferencia" && c !== "saldo_inicial").map((c) => <SelectItem key={c} value={c}>{CATEGORY_LABELS[c]}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Moneda">
            <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="MXN">MXN</SelectItem><SelectItem value="USD">USD</SelectItem></SelectContent>
            </Select>
          </F>
          <F label="Monto presupuestado"><Input type="number" step="0.01" value={form.budgeted_amount} onChange={(e) => setForm({ ...form, budgeted_amount: e.target.value })} data-testid="budget-amount" placeholder="0.00" /></F>
          <form onSubmit={submit}>
            <Button type="submit" data-testid="save-budget-btn" className="w-full bg-[#1E3A8A] hover:bg-[#1E40AF]"><Plus className="w-4 h-4 mr-1" /> Guardar</Button>
          </form>
        </div>
      </Card>

      <Card className="p-5 border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-outfit font-semibold text-sm uppercase tracking-[0.1em] text-stone-700 dark:text-stone-300">Presupuesto vs Real · <span className="text-[#1E3A8A]">{month}</span></h3>
          <span className="text-xs text-stone-500">{(vsActual.rows || []).length} categorías</span>
        </div>
        <div className="space-y-2.5">
          {(vsActual.rows || []).map((r) => {
            const b = budgets.find((x) => x.category === r.category);
            return (
              <div key={r.category} className="p-4 rounded-lg border border-stone-200 dark:border-stone-800 bg-stone-50/50 dark:bg-stone-900/30 hover:bg-white dark:hover:bg-stone-950 transition-colors" data-testid={`budget-row-${r.category}`}>
                <div className="flex items-center justify-between mb-2.5 flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <Target className="w-3.5 h-3.5 text-[#1E3A8A]" />
                    <span className="font-outfit font-semibold text-sm">{CATEGORY_LABELS[r.category] || r.category}</span>
                    {!r.has_budget && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 font-outfit font-semibold">Sin presupuesto</span>}
                  </div>
                  <div className="flex items-center gap-4 text-xs">
                    <div><span className="text-stone-500">Real: </span><span className="tabular-nums font-semibold text-stone-900 dark:text-stone-100">{money(r.actual_abs)}</span></div>
                    <div><span className="text-stone-500">Meta: </span><span className="tabular-nums text-stone-700 dark:text-stone-300">{money(r.budgeted)}</span></div>
                    {r.pct != null && <div className={`tabular-nums font-semibold px-2 py-0.5 rounded ${r.pct > 100 ? "bg-red-100 text-red-700" : r.pct > 80 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>{r.pct}%</div>}
                    {b && <button onClick={() => del(b.id)} className="p-1 rounded hover:bg-red-50 text-red-500 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></button>}
                  </div>
                </div>
                {r.budgeted > 0 && (
                  <div className="h-1.5 bg-stone-200 dark:bg-stone-800 rounded-full overflow-hidden">
                    <div className={`h-full transition-all ${r.pct > 100 ? "bg-red-500" : r.pct > 80 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(100, r.pct)}%` }}></div>
                  </div>
                )}
              </div>
            );
          })}
          {(vsActual.rows || []).length === 0 && (
            <div className="text-center py-10">
              <Target className="w-10 h-10 mx-auto text-stone-300 mb-2" strokeWidth={1.5} />
              <div className="text-sm text-stone-500">Sin movimientos ni presupuestos para <b>{month}</b>.</div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

// ============================================================
// CONCILIACION
// ============================================================
const ConciliacionTab = ({ movements, accounts, onLoad, onChange, filters, setFilters }) => {
  const [selected, setSelected] = useState(new Set());
  const [file, setFile] = useState(null);
  const [importAccount, setImportAccount] = useState("");
  const [importing, setImporting] = useState(false);

  useEffect(() => { onLoad(); }, []);

  const toggle = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };

  const conciliate = async (reconcile = true) => {
    const ids = Array.from(selected);
    if (!ids.length) return toast.error("Selecciona movimientos");
    try {
      await api.post(reconcile ? "/treasury/reconcile" : "/treasury/unreconcile", ids);
      toast.success(`${ids.length} movimientos ${reconcile ? "conciliados" : "desconciliados"}`);
      setSelected(new Set());
      onChange();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const doImport = async () => {
    if (!file) return toast.error("Elige archivo CSV");
    if (!importAccount) return toast.error("Elige cuenta");
    setImporting(true);
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter(Boolean);
      // Espera formato CSV: date,description,amount,reference
      const rows = lines.slice(lines[0].toLowerCase().includes("date") ? 1 : 0).map((line) => {
        const parts = line.split(",").map((s) => s.replace(/^"|"$/g, "").trim());
        return { date: parts[0], description: parts[1] || "", amount: parseFloat(parts[2] || 0), reference: parts[3] || "" };
      }).filter((r) => r.date && !isNaN(r.amount));
      if (!rows.length) return toast.error("CSV inválido (esperado: date,description,amount,reference)");
      const { data } = await api.post("/treasury/import-statement", { account_id: importAccount, rows });
      toast.success(`Importados ${data.inserted} de ${data.total_received} movimientos`);
      setFile(null);
      setImportAccount("");
      onChange();
    } catch (err) { toast.error(err.response?.data?.detail || err.message || "Error"); }
    finally { setImporting(false); }
  };

  const unreconciled = movements.filter((m) => !m.reconciled);
  const reconciled = movements.filter((m) => m.reconciled);

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <h3 className="font-outfit font-semibold text-sm uppercase tracking-wider text-stone-700 mb-3 flex items-center gap-2"><Upload className="w-4 h-4" /> Importar estado de cuenta (CSV)</h3>
        <div className="flex flex-wrap gap-2 items-end">
          <F label="Cuenta">
            <Select value={importAccount} onValueChange={setImportAccount}>
              <SelectTrigger className="w-56" data-testid="import-account"><SelectValue placeholder="Elegir…" /></SelectTrigger>
              <SelectContent>{accounts.filter((a) => a.active !== false && a.type === "banco").map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Archivo CSV"><Input type="file" accept=".csv" onChange={(e) => setFile(e.target.files?.[0])} data-testid="import-file" /></F>
          <Button onClick={doImport} disabled={importing || !file || !importAccount} data-testid="import-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]">
            {importing ? "Importando…" : "Importar"}
          </Button>
          <div className="text-[10px] text-stone-500 ml-2">Formato: <code>date,description,amount,reference</code>. Monto negativo = egreso.</div>
        </div>
      </Card>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-2">
          <Select value={filters.account_id} onValueChange={(v) => setFilters({ ...filters, account_id: v })}>
            <SelectTrigger className="w-56"><SelectValue placeholder="Filtrar por cuenta" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => conciliate(true)} data-testid="reconcile-btn" disabled={!selected.size} className="bg-emerald-600 hover:bg-emerald-700"><CheckCircle2 className="w-4 h-4 mr-1" /> Conciliar seleccionados ({selected.size})</Button>
          <Button variant="outline" onClick={() => conciliate(false)} disabled={!selected.size}>Desconciliar</Button>
        </div>
      </div>

      <Card className="overflow-hidden">
        <div className="max-h-[550px] overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-100 dark:bg-stone-900 border-b sticky top-0">
              <tr>{["✓", "Folio", "Fecha", "Cuenta", "Descripción", "Monto", "Estado"].map((h) => <th key={h} className="px-2 py-2 text-left text-[10px] font-outfit font-semibold uppercase tracking-wider text-stone-600">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-stone-200 dark:divide-stone-800">
              {unreconciled.map((m) => (
                <tr key={m.id} className="hover:bg-stone-50 dark:hover:bg-stone-900/30">
                  <td className="px-2 py-2"><input type="checkbox" checked={selected.has(m.id)} onChange={() => toggle(m.id)} data-testid={`concile-check-${m.id}`} /></td>
                  <td className="px-2 py-2 text-xs font-mono text-[#1E3A8A]">{m.folio}</td>
                  <td className="px-2 py-2 text-xs">{m.movement_date}</td>
                  <td className="px-2 py-2 text-xs">{m.account_name}</td>
                  <td className="px-2 py-2 text-xs">{m.description}</td>
                  <td className={`px-2 py-2 text-xs tabular-nums font-semibold ${m.direction === "IN" ? "text-emerald-700" : "text-amber-700"}`}>{m.direction === "IN" ? "+" : "−"} {money(m.amount, m.currency)}</td>
                  <td className="px-2 py-2 text-xs text-stone-500">Pendiente</td>
                </tr>
              ))}
              {reconciled.map((m) => (
                <tr key={m.id} className="opacity-60">
                  <td className="px-2 py-2"><input type="checkbox" checked={selected.has(m.id)} onChange={() => toggle(m.id)} /></td>
                  <td className="px-2 py-2 text-xs font-mono">{m.folio}</td>
                  <td className="px-2 py-2 text-xs">{m.movement_date}</td>
                  <td className="px-2 py-2 text-xs">{m.account_name}</td>
                  <td className="px-2 py-2 text-xs">{m.description}</td>
                  <td className={`px-2 py-2 text-xs tabular-nums font-semibold ${m.direction === "IN" ? "text-emerald-700" : "text-amber-700"}`}>{m.direction === "IN" ? "+" : "−"} {money(m.amount, m.currency)}</td>
                  <td className="px-2 py-2 text-xs text-emerald-700 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Conciliado</td>
                </tr>
              ))}
              {movements.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-stone-500">Sin movimientos.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

// ============================================================
// FX WIDGET
// ============================================================
const FxWidget = ({ fx, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [rate, setRate] = useState(fx.usd_mxn);

  useEffect(() => { setRate(fx.usd_mxn); }, [fx.usd_mxn]);

  const save = async () => {
    try {
      await api.post("/treasury/fx-rate", { usd_mxn: parseFloat(rate) });
      toast.success(`TC USD/MXN actualizado a ${rate}`);
      setEditing(false); onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  return (
    <div className="flex items-center gap-2 px-3.5 py-2.5 rounded-lg bg-white dark:bg-stone-950 border border-stone-200 dark:border-stone-800 shadow-sm" data-testid="fx-widget">
      <div className="w-7 h-7 rounded-md bg-[#1E3A8A]/10 flex items-center justify-center">
        <RefreshCw className="w-3.5 h-3.5 text-[#1E3A8A]" />
      </div>
      <div className="flex flex-col">
        <span className="text-[9px] uppercase tracking-[0.1em] font-outfit font-semibold text-stone-500">TC USD/MXN</span>
        {editing ? (
          <div className="flex items-center gap-1.5 mt-0.5">
            <Input type="number" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} className="w-20 h-6 text-sm px-2" data-testid="fx-input" autoFocus />
            <button onClick={save} data-testid="fx-save" className="text-[10px] uppercase tracking-wider font-outfit font-semibold px-2 py-1 rounded bg-[#1E3A8A] text-white hover:bg-[#1E40AF]">Guardar</button>
            <button onClick={() => setEditing(false)} className="text-[10px] p-1 rounded hover:bg-stone-100 text-stone-500">×</button>
          </div>
        ) : (
          <div className="flex items-center gap-1.5">
            <span className="font-outfit font-bold tabular-nums text-lg text-stone-900 dark:text-stone-100 leading-none" data-testid="fx-rate-display">{fx.usd_mxn?.toFixed(2)}</span>
            <button onClick={() => setEditing(true)} className="p-1 rounded hover:bg-stone-100 dark:hover:bg-stone-800 text-stone-400 hover:text-stone-700" data-testid="fx-edit"><Pencil className="w-3 h-3" /></button>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================
// UI HELPERS (estilo consistente con el resto del ERP)
// ============================================================
const TONES = {
  blue: "bg-[#1E3A8A]/10 text-[#1E3A8A] dark:text-blue-400",
  amber: "bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400",
  emerald: "bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400",
  stone: "bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300",
  navy: "bg-[#1E3A8A]/10 text-[#1E3A8A] dark:text-blue-400",
};

const KPI = ({ icon: Icon, label, value, tone = "blue", sub }) => (
  <Card className="p-4 border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm">
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="text-[10px] font-outfit font-semibold uppercase tracking-[0.1em] text-stone-500 dark:text-stone-400">{label}</div>
        <div className="mt-1 font-outfit font-semibold text-xl text-stone-900 dark:text-stone-100 tabular-nums truncate">{value}</div>
        {sub && <div className="text-[10px] text-stone-500 mt-0.5">{sub}</div>}
      </div>
      <div className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${TONES[tone] || TONES.blue}`}><Icon className="w-4 h-4" strokeWidth={1.75} /></div>
    </div>
  </Card>
);

const F = ({ label, children, className = "" }) => (
  <div className={className}>
    <Label className="text-[10px] uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);

